import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Settings,
  Type,
  Image,
  LayoutGrid,
  MessageSquare,
  HelpCircle,
  Video,
  Minus,
  Users,
  MapPin,
  MousePointerClick,
  Columns
} from 'lucide-react';

// Block Type Icons
const blockTypeIcons = {
  hero: LayoutGrid,
  text: Type,
  image: Image,
  image_text: Columns,
  cta: MousePointerClick,
  features: LayoutGrid,
  faq: HelpCircle,
  contact_form: MessageSquare,
  gallery: Image,
  video: Video,
  spacer: Minus,
  divider: Minus,
  testimonials: Users,
  locations: MapPin
};

// Block Type Labels
const blockTypeLabels = {
  hero: 'Hero Banner',
  text: 'Text',
  image: 'Bild',
  image_text: 'Bild + Text',
  cta: 'Call-to-Action',
  features: 'Features / Vorteile',
  faq: 'FAQ Akkordeon',
  contact_form: 'Kontaktformular',
  gallery: 'Bildergalerie',
  video: 'Video',
  spacer: 'Abstand',
  divider: 'Trennlinie',
  testimonials: 'Bewertungen',
  locations: 'Standorte'
};

// Default block content
const defaultBlockContent = {
  hero: { title: 'Willkommen', subtitle: 'Untertitel hier einfügen', background_image: '', button_text: 'Mehr erfahren', button_link: '/kontakt', overlay_opacity: 0.5 },
  text: { content: '<p>Hier kommt dein Text...</p>', alignment: 'left' },
  image: { image_url: '', alt_text: '', caption: '', width: 'full' },
  image_text: { image_url: '', image_position: 'left', title: '', content: '', button_text: '', button_link: '' },
  cta: { title: 'Bereit loszulegen?', subtitle: 'Kontaktieren Sie uns noch heute', button_text: 'Jetzt anfragen', button_link: '/kontakt', background_color: 'blue' },
  features: { title: 'Unsere Vorteile', features: [{ icon: '✓', title: 'Vorteil 1', description: 'Beschreibung' }], columns: 3 },
  faq: { title: 'Häufig gestellte Fragen', faqs: [{ question: 'Frage?', answer: 'Antwort...' }] },
  contact_form: { title: 'Kontaktiere uns', subtitle: '', show_location_select: true, show_phone: true, show_message: true },
  gallery: { title: '', images: [], columns: 3 },
  video: { video_url: '', title: '' },
  spacer: { height: 'medium' },
  divider: { style: 'line' },
  testimonials: { title: 'Was unsere Kunden sagen', show_count: 3 },
  locations: { title: 'Unsere Standorte', show_map: true }
};

const PageBuilder = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API}/cms/pages/${slug}`);
      setPage(res.data);
    } catch (err) {
      toast.error('Seite nicht gefunden');
      navigate('/admin/seiten');
    } finally {
      setLoading(false);
    }
  };

  const savePage = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/pages/${slug}`, page, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      toast.success('Seite gespeichert');
      // Update slug in URL if changed
      if (page.slug !== slug) {
        navigate(`/admin/seiten/${page.slug}`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: Date.now().toString(),
      type,
      ...defaultBlockContent[type]
    };
    setPage({
      ...page,
      blocks: [...(page.blocks || []), newBlock]
    });
    setShowAddBlock(false);
    setEditingBlock(newBlock.id);
  };

  const updateBlock = (blockId, updates) => {
    setPage({
      ...page,
      blocks: page.blocks.map(b => 
        b.id === blockId ? { ...b, ...updates } : b
      )
    });
  };

  const deleteBlock = (blockId) => {
    if (!window.confirm('Block wirklich löschen?')) return;
    setPage({
      ...page,
      blocks: page.blocks.filter(b => b.id !== blockId)
    });
    if (editingBlock === blockId) setEditingBlock(null);
  };

  const moveBlock = (blockId, direction) => {
    const blocks = [...page.blocks];
    const index = blocks.findIndex(b => b.id === blockId);
    if (direction === 'up' && index > 0) {
      [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
    } else if (direction === 'down' && index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
    }
    setPage({ ...page, blocks });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!page) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="page-builder">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/seiten')} data-testid="back-to-pages">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div>
              <h1 className="font-semibold text-slate-900">{page.title}</h1>
              <p className="text-sm text-slate-500">/{page.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowSettings(true)}
              data-testid="page-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
            {page.published && (
              <Button 
                variant="outline"
                onClick={() => window.open(`/seite/${page.slug}`, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Vorschau
              </Button>
            )}
            <Button 
              onClick={savePage} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-page-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Blocks */}
        <div className="space-y-4">
          {page.blocks?.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <LayoutGrid className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Keine Blöcke vorhanden</h3>
                <p className="text-slate-500 mb-4">Füge deinen ersten Inhaltsblock hinzu</p>
                <Button onClick={() => setShowAddBlock(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Block hinzufügen
                </Button>
              </CardContent>
            </Card>
          )}

          {page.blocks?.map((block, index) => (
            <BlockEditor
              key={block.id}
              block={block}
              isEditing={editingBlock === block.id}
              onEdit={() => setEditingBlock(editingBlock === block.id ? null : block.id)}
              onUpdate={(updates) => updateBlock(block.id, updates)}
              onDelete={() => deleteBlock(block.id)}
              onMoveUp={() => moveBlock(block.id, 'up')}
              onMoveDown={() => moveBlock(block.id, 'down')}
              isFirst={index === 0}
              isLast={index === page.blocks.length - 1}
            />
          ))}

          {/* Add Block Button */}
          <Button 
            onClick={() => setShowAddBlock(true)} 
            variant="outline" 
            className="w-full border-dashed h-16"
            data-testid="add-block-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Block hinzufügen
          </Button>
        </div>
      </div>

      {/* Add Block Dialog */}
      <Dialog open={showAddBlock} onOpenChange={setShowAddBlock}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Block hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-4">
            {Object.entries(blockTypeLabels).map(([type, label]) => {
              const Icon = blockTypeIcons[type];
              return (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  data-testid={`add-block-${type}`}
                >
                  <Icon className="w-6 h-6 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Page Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Seiten-Einstellungen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seitentitel</Label>
              <Input
                value={page.title}
                onChange={(e) => setPage({...page, title: e.target.value})}
                data-testid="settings-title"
              />
            </div>
            <div className="space-y-2">
              <Label>URL-Pfad (Slug)</Label>
              <div className="flex items-center">
                <span className="text-sm text-slate-500 mr-2">/seite/</span>
                <Input
                  value={page.slug}
                  onChange={(e) => setPage({...page, slug: e.target.value})}
                  data-testid="settings-slug"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Meta-Titel (SEO)</Label>
              <Input
                value={page.meta_title || ''}
                onChange={(e) => setPage({...page, meta_title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Meta-Beschreibung (SEO)</Label>
              <Textarea
                value={page.meta_description || ''}
                onChange={(e) => setPage({...page, meta_description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Veröffentlicht</Label>
                <p className="text-sm text-slate-500">Seite ist öffentlich sichtbar</p>
              </div>
              <Switch
                checked={page.published}
                onCheckedChange={(checked) => setPage({...page, published: checked})}
                data-testid="settings-published"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Schließen</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Block Editor Component
const BlockEditor = ({ block, isEditing, onEdit, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const Icon = blockTypeIcons[block.type] || Type;
  const label = blockTypeLabels[block.type] || block.type;

  return (
    <Card className={`transition-all ${isEditing ? 'ring-2 ring-blue-500' : ''}`} data-testid={`block-${block.id}`}>
      {/* Block Header */}
      <div className="flex items-center justify-between p-3 bg-slate-50 border-b">
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-sm text-slate-700">{label}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onMoveUp} disabled={isFirst}>
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onMoveDown} disabled={isLast}>
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Block Preview / Editor */}
      {isEditing ? (
        <CardContent className="p-4">
          <BlockSettings block={block} onUpdate={onUpdate} />
        </CardContent>
      ) : (
        <CardContent className="p-4">
          <BlockPreview block={block} />
        </CardContent>
      )}
    </Card>
  );
};

// Block Preview Component
const BlockPreview = ({ block }) => {
  switch (block.type) {
    case 'hero':
      return (
        <div className="bg-slate-100 rounded-lg p-4 text-center">
          <h3 className="font-bold text-lg">{block.title || 'Hero Titel'}</h3>
          <p className="text-slate-500">{block.subtitle || 'Untertitel'}</p>
          {block.button_text && (
            <span className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded">{block.button_text}</span>
          )}
        </div>
      );
    case 'text':
      return (
        <div 
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: block.content || '<p>Textinhalt...</p>' }}
        />
      );
    case 'image':
      return block.image_url ? (
        <img src={block.image_url} alt={block.alt_text} className="max-h-32 rounded" />
      ) : (
        <div className="bg-slate-100 h-24 rounded flex items-center justify-center text-slate-400">
          <Image className="w-8 h-8" />
        </div>
      );
    case 'cta':
      return (
        <div className={`rounded-lg p-4 text-center bg-${block.background_color || 'blue'}-100`}>
          <h4 className="font-bold">{block.title}</h4>
          <p className="text-sm text-slate-600">{block.subtitle}</p>
        </div>
      );
    case 'features':
      return (
        <div className="grid grid-cols-3 gap-2">
          {(block.features || []).slice(0, 3).map((f, i) => (
            <div key={i} className="bg-slate-50 p-2 rounded text-center text-sm">
              <span className="block font-medium">{f.title}</span>
            </div>
          ))}
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-1">
          {(block.faqs || []).slice(0, 2).map((f, i) => (
            <div key={i} className="bg-slate-50 p-2 rounded text-sm">
              <span className="font-medium">{f.question}</span>
            </div>
          ))}
        </div>
      );
    case 'contact_form':
      return (
        <div className="bg-slate-50 p-4 rounded text-center text-slate-500">
          <MessageSquare className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm">Kontaktformular</span>
        </div>
      );
    case 'testimonials':
      return (
        <div className="bg-slate-50 p-4 rounded text-center text-slate-500">
          <Users className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm">Zeigt {block.show_count || 3} Bewertungen</span>
        </div>
      );
    case 'locations':
      return (
        <div className="bg-slate-50 p-4 rounded text-center text-slate-500">
          <MapPin className="w-6 h-6 mx-auto mb-2" />
          <span className="text-sm">Standort-Übersicht</span>
        </div>
      );
    case 'spacer':
      return (
        <div className={`bg-slate-100 rounded ${block.height === 'small' ? 'h-4' : block.height === 'large' ? 'h-16' : 'h-8'}`}></div>
      );
    case 'divider':
      return <hr className="border-slate-200" />;
    default:
      return <div className="text-slate-400 text-sm">Block: {block.type}</div>;
  }
};

// Block Settings Component
const BlockSettings = ({ block, onUpdate }) => {
  switch (block.type) {
    case 'hero':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Untertitel</Label>
            <Input value={block.subtitle || ''} onChange={(e) => onUpdate({ subtitle: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Hintergrundbild URL</Label>
            <Input value={block.background_image || ''} onChange={(e) => onUpdate({ background_image: e.target.value })} placeholder="https://..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input value={block.button_text || ''} onChange={(e) => onUpdate({ button_text: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input value={block.button_link || ''} onChange={(e) => onUpdate({ button_link: e.target.value })} />
            </div>
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Inhalt (HTML erlaubt)</Label>
            <Textarea 
              value={block.content || ''} 
              onChange={(e) => onUpdate({ content: e.target.value })} 
              rows={6}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Ausrichtung</Label>
            <Select value={block.alignment || 'left'} onValueChange={(v) => onUpdate({ alignment: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Links</SelectItem>
                <SelectItem value="center">Zentriert</SelectItem>
                <SelectItem value="right">Rechts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Bild URL</Label>
            <Input value={block.image_url || ''} onChange={(e) => onUpdate({ image_url: e.target.value })} placeholder="https://..." />
          </div>
          <div className="space-y-2">
            <Label>Alt-Text</Label>
            <Input value={block.alt_text || ''} onChange={(e) => onUpdate({ alt_text: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Bildunterschrift</Label>
            <Input value={block.caption || ''} onChange={(e) => onUpdate({ caption: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Breite</Label>
            <Select value={block.width || 'full'} onValueChange={(v) => onUpdate({ width: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Klein</SelectItem>
                <SelectItem value="medium">Mittel</SelectItem>
                <SelectItem value="full">Volle Breite</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Untertitel</Label>
            <Input value={block.subtitle || ''} onChange={(e) => onUpdate({ subtitle: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input value={block.button_text || ''} onChange={(e) => onUpdate({ button_text: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input value={block.button_link || ''} onChange={(e) => onUpdate({ button_link: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Hintergrundfarbe</Label>
            <Select value={block.background_color || 'blue'} onValueChange={(v) => onUpdate({ background_color: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="blue">Blau</SelectItem>
                <SelectItem value="green">Grün</SelectItem>
                <SelectItem value="orange">Orange</SelectItem>
                <SelectItem value="gray">Grau</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'features':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Spalten</Label>
            <Select value={String(block.columns || 3)} onValueChange={(v) => onUpdate({ columns: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 Spalten</SelectItem>
                <SelectItem value="3">3 Spalten</SelectItem>
                <SelectItem value="4">4 Spalten</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Features</Label>
            {(block.features || []).map((feature, index) => (
              <div key={index} className="flex gap-2 items-start">
                <Input 
                  value={feature.icon || ''} 
                  onChange={(e) => {
                    const updated = [...block.features];
                    updated[index] = { ...feature, icon: e.target.value };
                    onUpdate({ features: updated });
                  }}
                  className="w-16"
                  placeholder="Icon"
                />
                <Input 
                  value={feature.title || ''} 
                  onChange={(e) => {
                    const updated = [...block.features];
                    updated[index] = { ...feature, title: e.target.value };
                    onUpdate({ features: updated });
                  }}
                  placeholder="Titel"
                />
                <Input 
                  value={feature.description || ''} 
                  onChange={(e) => {
                    const updated = [...block.features];
                    updated[index] = { ...feature, description: e.target.value };
                    onUpdate({ features: updated });
                  }}
                  placeholder="Beschreibung"
                />
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    const updated = block.features.filter((_, i) => i !== index);
                    onUpdate({ features: updated });
                  }}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onUpdate({ features: [...(block.features || []), { icon: '✓', title: '', description: '' }] })}
            >
              <Plus className="w-4 h-4 mr-2" /> Feature hinzufügen
            </Button>
          </div>
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>FAQs</Label>
            {(block.faqs || []).map((faq, index) => (
              <div key={index} className="border rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-sm">Frage {index + 1}</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      const updated = block.faqs.filter((_, i) => i !== index);
                      onUpdate({ faqs: updated });
                    }}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Input 
                  value={faq.question || ''} 
                  onChange={(e) => {
                    const updated = [...block.faqs];
                    updated[index] = { ...faq, question: e.target.value };
                    onUpdate({ faqs: updated });
                  }}
                  placeholder="Frage"
                />
                <Textarea 
                  value={faq.answer || ''} 
                  onChange={(e) => {
                    const updated = [...block.faqs];
                    updated[index] = { ...faq, answer: e.target.value };
                    onUpdate({ faqs: updated });
                  }}
                  placeholder="Antwort"
                  rows={2}
                />
              </div>
            ))}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onUpdate({ faqs: [...(block.faqs || []), { question: '', answer: '' }] })}
            >
              <Plus className="w-4 h-4 mr-2" /> FAQ hinzufügen
            </Button>
          </div>
        </div>
      );

    case 'contact_form':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Untertitel</Label>
            <Input value={block.subtitle || ''} onChange={(e) => onUpdate({ subtitle: e.target.value })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Standort-Auswahl anzeigen</Label>
            <Switch checked={block.show_location_select !== false} onCheckedChange={(v) => onUpdate({ show_location_select: v })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Telefon-Feld anzeigen</Label>
            <Switch checked={block.show_phone !== false} onCheckedChange={(v) => onUpdate({ show_phone: v })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Nachricht-Feld anzeigen</Label>
            <Switch checked={block.show_message !== false} onCheckedChange={(v) => onUpdate({ show_message: v })} />
          </div>
        </div>
      );

    case 'spacer':
      return (
        <div className="space-y-2">
          <Label>Höhe</Label>
          <Select value={block.height || 'medium'} onValueChange={(v) => onUpdate({ height: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="small">Klein (16px)</SelectItem>
              <SelectItem value="medium">Mittel (32px)</SelectItem>
              <SelectItem value="large">Groß (64px)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );

    case 'testimonials':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Anzahl Bewertungen</Label>
            <Select value={String(block.show_count || 3)} onValueChange={(v) => onUpdate({ show_count: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="4">4</SelectItem>
                <SelectItem value="6">6</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'locations':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Karte anzeigen</Label>
            <Switch checked={block.show_map !== false} onCheckedChange={(v) => onUpdate({ show_map: v })} />
          </div>
        </div>
      );

    case 'video':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Video URL (YouTube/Vimeo)</Label>
            <Input value={block.video_url || ''} onChange={(e) => onUpdate({ video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
          </div>
          <div className="space-y-2">
            <Label>Titel (optional)</Label>
            <Input value={block.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
          </div>
        </div>
      );

    default:
      return <div className="text-slate-500">Keine Einstellungen verfügbar</div>;
  }
};

export default PageBuilder;
